import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { puedeEditarPagina } from "../services/permisoService";
import { setAccesos, setParametros, setPerfil } from "../redux/seguridadSlice";
import * as seguridadService from "../services/seguridadService";

/**
 * Estado de edicion comun de todas las pantallas de mantenimiento.
 *
 * Las pantallas se entran con isReadOnly y lo pasan a cada componente para
 * deshabilitar todos sus controles. El permiso sale de los accesos del usuario
 * (seguridad) y, con la seguridad desactivada, de permisoService. El bloqueo
 * se activa al guardar, tal como en Person/Edit.
 *
 * @param {string|string[]} pagina Llave de la pagina, por ejemplo "periodo".
 *   Puede recibir varias: el permiso se concede con que el usuario tenga
 *   cualquiera de ellas. La ventana de sesion usa ["sesion",
 *   "sesionTerapeuta"] porque el terapeuta registra sus propias sesiones con
 *   sesionTerapeuta.editar y la supervision las edita con sesion.editar.
 */
export const useMantenimiento = (pagina) => {
    const dispatch = useDispatch();
    const paginas = Array.isArray(pagina) ? pagina : [pagina];
    const accesos = useSelector((state) => state.seguridad?.accesos) || [];
    const seguridadActiva = useSelector((state) => state.seguridad?.seguridadActiva) || "N";
    // La filial del usuario: con 0 ve todas las sedes. Los filtros de filial
    // de las ventanas de navegacion ofrecen solo la suya.
    const idFilial = useSelector((state) => state.seguridad?.idFilial) || 0;
    const perfilCargado = useSelector((state) => state.seguridad?.perfilCargado) || false;
    const usuarioPerfil = useSelector((state) => state.seguridad?.usuarioPerfil) || "";

    // Se consultan los accesos, los parametros de la seguridad y el perfil
    // para el usuario autenticado en la sesion actual.
    useEffect(() => {
        const usuario = sessionStorage.getItem("sisdet.usuario");
        if (!usuario) return;

        const usuarioNorm = usuario.toLowerCase();
        const perfilValido = perfilCargado && usuarioPerfil === usuarioNorm;

        if (accesos.length > 0 && perfilValido) return;

        seguridadService.getsParametros()
            .then((parametros) => dispatch(setParametros(parametros)))
            .catch((err) => { console.log("Err: ", err); });

        seguridadService.getsPerfil()
            .then((perfil) => dispatch(setPerfil(perfil)))
            .catch((err) => { console.log("Err: ", err); });

        seguridadService.getsAccesosUsuario(usuario)
            .then((lista) => dispatch(setAccesos(lista || [])))
            .catch((err) => { console.log("Err: ", err); });
    }, [accesos.length, perfilCargado, usuarioPerfil, dispatch]);

    const [guardado, setGuardado] = useState(false);

    // Con la seguridad activa manda el acceso del usuario; si esta desactivada
    // se usa el permiso por defecto (hoy, todos pueden editar).
    //
    // El permiso de escritura es pagina.editar. Las pantallas que solo
    // registran pagina.nuevo (atencion, diagnostico, periodo) lo toman
    // tambien como permiso de escritura, porque en ellas crear el registro
    // es la unica accion de mantenimiento: no hay edicion separada de la
    // creacion.
    //
    // Si la pagina admite varias llaves, basta con tener una de ellas.
    const puedeEditar = seguridadActiva === "S"
        ? paginas.some((p) => accesos.includes(p) || accesos.includes(p + ".editar") || accesos.includes(p + ".nuevo"))
        : paginas.some((p) => puedeEditarPagina(p));

    const isReadOnly = !puedeEditar || guardado;

    const setIsReadOnly = useCallback((valor) => setGuardado(!!valor), []);
    const habilitarEdicion = useCallback(() => setGuardado(false), []);
    const bloquearEdicion = useCallback(() => setGuardado(true), []);

    return {
        pagina,
        puedeEditar,
        isReadOnly,
        // La filial del usuario (0 = ve todas). Los filtros de filial la
        // usan para limitarse a la suya.
        idFilial,
        // Si el perfil ya se consulto. Los filtros de filial esperan a que
        // llegue: si se armaran antes, se verian todas las sedes.
        perfilCargado,
        setIsReadOnly,
        habilitarEdicion,
        bloquearEdicion,
    };
};

export default useMantenimiento;
